/**
 * Bounded-concurrency map (issue #17). At boot the viewer pulls in the whole
 * ~180-tile AOI; loading every tile at once (`Promise.all`) lets many GLB
 * parses land in the same main-thread burst, which stalls the tab ("page not
 * responding" on weak hardware). Running at most `limit` at a time spreads the
 * parses out and keeps the event loop responsive, at a small cost to total
 * load time. Results are returned in input order, like `Promise.all`.
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
  options: { yieldBetween?: boolean } = {},
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  const workerCount = Math.max(1, Math.min(limit, items.length));

  async function worker(): Promise<void> {
    while (next < items.length) {
      const index = next++;
      results[index] = await fn(items[index], index);
      // Hand control back to the event loop between items. `await` on an
      // already-resolved promise (e.g. an HTTP-cached tile) only drains
      // microtasks, so without a real macrotask yield a run of cached tiles
      // parses back-to-back as ONE long task and freezes the tab. This is the
      // fix for the ~10s main-thread stall / "page not responding" at boot.
      if (options.yieldBetween) await yieldToEventLoop();
    }
  }

  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return results;
}

/** Resolves on the next macrotask, yielding to rendering/input — unlike a bare
 * awaited microtask. Uses MessageChannel (no setTimeout 4ms clamp) where
 * available, falling back to setTimeout. */
export function yieldToEventLoop(): Promise<void> {
  // Prefer scheduler.yield (Edge/Chrome) — yields to rendering/input at
  // user-visible priority with no timer clamp; then MessageChannel (a real
  // macrotask, also unclamped); finally setTimeout.
  const scheduler = (globalThis as { scheduler?: { yield?: () => Promise<void> } }).scheduler;
  if (scheduler?.yield) return scheduler.yield();
  if (typeof MessageChannel !== 'undefined') {
    return new Promise<void>((resolve) => {
      const channel = new MessageChannel();
      channel.port1.onmessage = () => {
        channel.port1.close();
        resolve();
      };
      channel.port2.postMessage(undefined);
    });
  }
  return new Promise<void>((resolve) => setTimeout(resolve, 0));
}
